import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from './Layout';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/api';
import environment from '../config/environment';
import { prepareImageForUpload } from '../utils/compressImage';

const FILTRO_PERSONAL_JUSTIFICACION = 'activo';

/** Alineado con Multer en justificacion-periodo-subir (6 MB). */
const JUSTIFICACION_MAX_BYTES = 6 * 1024 * 1024;

const isMobileDeviceForCamera = () => {
  if (typeof window === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

/** Ventana ±N días desde hoy para Desde y Hasta (alineado con backend). */
const CERT_PERIODO_MAX_DIAS = 30;

const normalizeSearch = (s) =>
  String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const normalizeCedulaCert = (cedula) => {
  const d = String(cedula || '').replace(/\D/g, '');
  if (d.length >= 10) return d.slice(-10);
  return d.padStart(10, '0');
};

const loginEsMismaCedulaSeleccion = (username, cedulaRef) => {
  const loginDigits = String(username || '').replace(/\D/g, '');
  if (loginDigits.length < 10) return false;
  return normalizeCedulaCert(username) === normalizeCedulaCert(cedulaRef);
};

const toYmd = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const limitesFechasCertificado = () => {
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const min = new Date(hoy);
  min.setDate(min.getDate() - CERT_PERIODO_MAX_DIAS);
  const max = new Date(hoy);
  max.setDate(max.getDate() + CERT_PERIODO_MAX_DIAS);
  return { min: toYmd(min), max: toYmd(max), hoy: toYmd(hoy) };
};

const CertificadoMultaFaltaPunto = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { min: fechaMin, max: fechaMax, hoy: fechaHoy } = limitesFechasCertificado();

  const esProduccion = environment.isProduction;
  const [personalLista, setPersonalLista] = useState([]);
  const [cedulaSeleccionada, setCedulaSeleccionada] = useState('');
  const [personaSearch, setPersonaSearch] = useState('');
  const [showPersonaResults, setShowPersonaResults] = useState(false);
  /** Tras pulsar Buscar: muestra historial y botón para abrir el formulario. */
  const [personaConsultada, setPersonaConsultada] = useState(false);
  const [formularioRegistroAbierto, setFormularioRegistroAbierto] = useState(false);
  const [fechaDesde, setFechaDesde] = useState(fechaHoy);
  const [fechaHasta, setFechaHasta] = useState(fechaHoy);

  const [loadingLista, setLoadingLista] = useState(false);
  const [loadingHistorial, setLoadingHistorial] = useState(false);
  const [historial, setHistorial] = useState(null);
  const [error, setError] = useState('');

  const [archivo, setArchivo] = useState(null);
  const [loadingSubida, setLoadingSubida] = useState(false);
  const [msgSubida, setMsgSubida] = useState('');
  const [errSubida, setErrSubida] = useState('');
  const [confirmarSubidaAbierta, setConfirmarSubidaAbierta] = useState(false);
  const [modalExito, setModalExito] = useState(false);

  const [vistaCertMeta, setVistaCertMeta] = useState(null);
  const [vistaCertImg, setVistaCertImg] = useState({ loading: false, error: null, url: null });
  const [cameraCertAbierta, setCameraCertAbierta] = useState(false);

  const certVideoRef = useRef(null);
  const certCanvasRef = useRef(null);
  const certStreamRef = useRef(null);
  const certInputCameraRef = useRef(null);
  const certInputFileRef = useRef(null);

  const useDeviceCamera = isMobileDeviceForCamera();

  const nickSubidor = useMemo(
    () => String(user?.nick || user?.firstName || user?.login_nombre || '').trim(),
    [user?.nick, user?.firstName, user?.login_nombre]
  );

  const bloqueoJustificacionPropio = useMemo(
    () =>
      Boolean(
        cedulaSeleccionada
          && (loginEsMismaCedulaSeleccion(user?.username, cedulaSeleccionada)
            || loginEsMismaCedulaSeleccion(user?.cedula, cedulaSeleccionada))
      ),
    [user?.username, user?.cedula, cedulaSeleccionada]
  );

  const nombrePersonaSeleccionada = useMemo(() => {
    const p = personalLista.find((x) => String(x.cedula) === String(cedulaSeleccionada));
    return String(p?.nombres || '').trim() || 'Persona seleccionada';
  }, [personalLista, cedulaSeleccionada]);

  const resultadosPersona = useMemo(() => {
    if (cedulaSeleccionada) return [];
    const q = normalizeSearch(personaSearch);
    if (!q) return [];
    return personalLista
      .filter((p) => {
        const nombre = normalizeSearch(p.nombres);
        const ced = normalizeSearch(p.cedula);
        return nombre.includes(q) || ced.includes(q);
      })
      .slice(0, 40);
  }, [personalLista, personaSearch, cedulaSeleccionada]);

  const seleccionarPersona = useCallback((p) => {
    setCedulaSeleccionada(String(p.cedula || ''));
    setPersonaSearch(String(p.nombres || '').trim());
    setShowPersonaResults(false);
  }, []);

  const limpiarPersona = useCallback(() => {
    setCedulaSeleccionada('');
    setPersonaSearch('');
    setShowPersonaResults(false);
  }, []);

  const stopCameraCert = useCallback(() => {
    if (certStreamRef.current) {
      certStreamRef.current.getTracks().forEach((t) => t.stop());
      certStreamRef.current = null;
    }
    setCameraCertAbierta(false);
  }, []);

  useEffect(() => () => stopCameraCert(), [stopCameraCert]);

  useEffect(() => {
    if (!cameraCertAbierta || !certStreamRef.current || !certVideoRef.current) return;
    const video = certVideoRef.current;
    video.srcObject = certStreamRef.current;
    video.play().catch(() => {});
  }, [cameraCertAbierta]);

  const startCameraCertDesktop = useCallback(async () => {
    stopCameraCert();
    setErrSubida('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      certStreamRef.current = stream;
      setCameraCertAbierta(true);
    } catch (err) {
      console.error(err);
      setErrSubida('No se pudo acceder a la cámara.');
      setCameraCertAbierta(false);
    }
  }, [stopCameraCert]);

  const captureFromCertVideo = useCallback(async () => {
    const video = certVideoRef.current;
    const canvas = certCanvasRef.current;
    if (!video || !canvas || !video.srcObject) return null;
    const ctx = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0);
    const blob = await new Promise((resolve) => {
      canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.92);
    });
    if (!blob) return null;
    const raw = new File([blob], 'justificacion-captura.jpg', { type: 'image/jpeg' });
    const prepared = await prepareImageForUpload(raw, {
      maxBytes: JUSTIFICACION_MAX_BYTES,
      label: 'imagen del certificado'
    });
    if (!prepared.ok) {
      setErrSubida(prepared.error);
      return null;
    }
    return prepared.file;
  }, []);

  const handleCertFileFromInput = useCallback(async (e) => {
    const input = e.target;
    const file = input.files?.[0] || null;
    setErrSubida('');
    if (!file) {
      setArchivo(null);
      return;
    }
    const prepared = await prepareImageForUpload(file, {
      maxBytes: JUSTIFICACION_MAX_BYTES,
      label: 'imagen del certificado'
    });
    if (!prepared.ok) {
      setArchivo(null);
      setErrSubida(prepared.error);
      input.value = '';
      return;
    }
    setArchivo(prepared.file);
  }, []);

  const previewObjectUrl = useMemo(() => {
    if (!archivo) return null;
    return URL.createObjectURL(archivo);
  }, [archivo]);

  useEffect(() => {
    return () => {
      if (previewObjectUrl) URL.revokeObjectURL(previewObjectUrl);
    };
  }, [previewObjectUrl]);

  useEffect(() => {
    if (!vistaCertMeta || !user?.username) {
      setVistaCertImg((prev) => {
        if (prev.url) URL.revokeObjectURL(prev.url);
        return { loading: false, error: null, url: null };
      });
      return undefined;
    }

    let cancelled = false;
    const controller = new AbortController();

    setVistaCertImg((prev) => {
      if (prev.url) URL.revokeObjectURL(prev.url);
      return { loading: true, error: null, url: null };
    });

    const imgReq = new URL(getApiUrl('SUPERVISION', 'JUSTIFICACION_PERIODO_IMAGEN'));
    imgReq.searchParams.set('username', user.username);
    imgReq.searchParams.set('id', String(vistaCertMeta.certId));

    fetch(imgReq.toString(), { credentials: 'include', signal: controller.signal })
      .then(async (res) => {
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error(j.error || (res.status === 404 ? 'Imagen no encontrada.' : `Error ${res.status}`));
        }
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        setVistaCertImg({ loading: false, error: null, url: URL.createObjectURL(blob) });
      })
      .catch((err) => {
        if (cancelled || err.name === 'AbortError') return;
        setVistaCertImg({ loading: false, error: err.message || 'No se pudo cargar la imagen.', url: null });
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [vistaCertMeta, user?.username]);

  useEffect(() => {
    if (user && user.rol !== 'S') {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const cargarPersonal = useCallback(async () => {
    if (!user?.username) {
      setPersonalLista([]);
      return;
    }
    setLoadingLista(true);
    setError('');
    try {
      const url = new URL(getApiUrl('SUPERVISION', 'PERSONAL_CERTIFICADO_LISTA'));
      url.searchParams.set('username', user.username);
      url.searchParams.set('filtro', FILTRO_PERSONAL_JUSTIFICACION);
      const res = await fetch(url.toString(), { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        setError(data.error || 'No se pudo cargar el personal.');
        setPersonalLista([]);
        return;
      }
      setPersonalLista(Array.isArray(data.data) ? data.data : []);
      setCedulaSeleccionada((prev) => {
        if (!prev) return prev;
        const existe = (data.data || []).some((p) => String(p.cedula) === String(prev));
        if (!existe) {
          setPersonaSearch('');
          return '';
        }
        return prev;
      });
    } catch (e) {
      setError(e.message || 'Error de conexión al cargar personal.');
      setPersonalLista([]);
    } finally {
      setLoadingLista(false);
    }
  }, [user?.username]);

  useEffect(() => {
    if (user?.rol === 'S') cargarPersonal();
  }, [cargarPersonal, user?.rol]);

  const cargarHistorial = useCallback(async () => {
    if (!cedulaSeleccionada || !user?.username) {
      setHistorial(null);
      return;
    }
    setLoadingHistorial(true);
    try {
      const url = new URL(getApiUrl('SUPERVISION', 'JUSTIFICACION_PERIODO_LISTA'));
      url.searchParams.set('username', user.username);
      url.searchParams.set('cedula', cedulaSeleccionada);
      const res = await fetch(url.toString(), { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        setError(data.error || 'No se pudo cargar el historial.');
        setHistorial([]);
        return;
      }
      setHistorial(Array.isArray(data.data) ? data.data : []);
    } catch (e) {
      setError(e.message || 'Error al cargar historial.');
      setHistorial([]);
    } finally {
      setLoadingHistorial(false);
    }
  }, [cedulaSeleccionada, user?.username]);

  useEffect(() => {
    setPersonaConsultada(false);
    setFormularioRegistroAbierto(false);
    setHistorial(null);
    setError('');
    setMsgSubida('');
    setErrSubida('');
    setArchivo(null);
    stopCameraCert();
  }, [cedulaSeleccionada, stopCameraCert]);

  const handleBuscarPersona = async (e) => {
    e.preventDefault();
    setError('');
    setErrSubida('');
    if (!cedulaSeleccionada) {
      setError('Seleccione una persona de la lista.');
      return;
    }
    setPersonaConsultada(true);
    setFormularioRegistroAbierto(false);
    setFechaDesde(fechaHoy);
    setFechaHasta(fechaHoy);
    setArchivo(null);
    stopCameraCert();
    await cargarHistorial();
  };

  const abrirFormularioRegistro = () => {
    setErrSubida('');
    setFechaDesde(fechaHoy);
    setFechaHasta(fechaHoy);
    setArchivo(null);
    stopCameraCert();
    setFormularioRegistroAbierto(true);
  };

  const cerrarFormularioRegistro = () => {
    setFormularioRegistroAbierto(false);
    setErrSubida('');
    setConfirmarSubidaAbierta(false);
    setArchivo(null);
    stopCameraCert();
  };

  const cerrarVistaCertificado = () => {
    setVistaCertMeta(null);
    setVistaCertImg((prev) => {
      if (prev.url) URL.revokeObjectURL(prev.url);
      return { loading: false, error: null, url: null };
    });
  };

  const abrirVistaCertificado = (row) => {
    setVistaCertMeta({
      certId: row.id,
      nombrePersona: nombrePersonaSeleccionada,
      fechaDesde: row.fecha_desde,
      fechaHasta: row.fecha_hasta,
      subidoEn: row.foto_subida_en,
      subidoPor: row.foto_subida_por
    });
  };

  const validarFormulario = () => {
    if (!cedulaSeleccionada) {
      setErrSubida('Seleccione una persona.');
      return false;
    }
    if (bloqueoJustificacionPropio) {
      setErrSubida('No puede justificar su propia cédula.');
      return false;
    }
    if (!fechaDesde || !fechaHasta) {
      setErrSubida('Indique fecha desde y fecha hasta.');
      return false;
    }
    if (fechaDesde > fechaHasta) {
      setErrSubida('La fecha inicio no puede ser posterior a la fecha fin.');
      return false;
    }
    if (
      fechaDesde < fechaMin
      || fechaDesde > fechaMax
      || fechaHasta < fechaMin
      || fechaHasta > fechaMax
    ) {
      setErrSubida(
        `Las fechas deben estar entre ${fechaMin} y ${fechaMax} (hoy ± ${CERT_PERIODO_MAX_DIAS} días).`
      );
      return false;
    }
    if (!archivo) {
      setErrSubida('Adjunte el documento (imagen).');
      return false;
    }
    return true;
  };

  const solicitarConfirmacionRegistro = (e) => {
    e.preventDefault();
    setErrSubida('');
    setMsgSubida('');
    if (!validarFormulario()) return;
    setConfirmarSubidaAbierta(true);
  };

  const ejecutarSubidaJustificacion = async () => {
    if (!archivo) return;
    setErrSubida('');
    setMsgSubida('');
    const formData = new FormData();
    formData.append('username', user?.username || '');
    formData.append('cedula', cedulaSeleccionada);
    formData.append('fecha_desde', fechaDesde);
    formData.append('fecha_hasta', fechaHasta);
    formData.append('archivo', archivo);
    if (nickSubidor) formData.append('subidoPor', nickSubidor);
    formData.append('origenSistema', 'supervisor');

    setLoadingSubida(true);
    try {
      const res = await fetch(getApiUrl('SUPERVISION', 'JUSTIFICACION_PERIODO_SUBIR'), {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        setConfirmarSubidaAbierta(false);
        if (res.status === 413) {
          setErrSubida(
            data.error
              || 'La imagen es demasiado grande para el servidor (413). Se comprimió en el dispositivo; intente de nuevo o use otra foto.'
          );
        } else {
          setErrSubida(data.error || `Error (${res.status}).`);
        }
        return;
      }
      setConfirmarSubidaAbierta(false);
      setArchivo(null);
      if (certInputCameraRef.current) certInputCameraRef.current.value = '';
      if (certInputFileRef.current) certInputFileRef.current.value = '';
      stopCameraCert();
      setFechaDesde(fechaHoy);
      setFechaHasta(fechaHoy);
      await cargarHistorial();
      setFormularioRegistroAbierto(false);
      setModalExito(true);
    } catch (err) {
      setConfirmarSubidaAbierta(false);
      setErrSubida(err.message || 'Error de conexión.');
    } finally {
      setLoadingSubida(false);
    }
  };

  if (!user || user.rol !== 'S') {
    return null;
  }

  return (
    <Layout>
      <div className="max-w-xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-6 sm:p-8">
          <div className="flex flex-col items-center text-center mb-6">
            <div className="w-14 h-14 rounded-full bg-indigo-100 flex items-center justify-center mb-3">
              <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Certificados médicos</h1>
            <p className="text-sm text-gray-500 mt-1">
              Seleccione personal y pulse Buscar; luego puede registrar un nuevo certificado médico
            </p>
          </div>

          <form onSubmit={handleBuscarPersona} className="space-y-5">
            <div>
              <label htmlFor="cert-personal" className="block text-sm font-medium text-gray-800 mb-1">
                Personal <span className="text-red-600">*</span>
              </label>
              <div className="relative">
                <input
                  id="cert-personal"
                  type="text"
                  value={cedulaSeleccionada ? nombrePersonaSeleccionada : personaSearch}
                  onChange={(e) => {
                    setCedulaSeleccionada('');
                    setPersonaSearch(e.target.value);
                    setShowPersonaResults(true);
                  }}
                  onFocus={() => {
                    if (!cedulaSeleccionada && personaSearch.trim()) setShowPersonaResults(true);
                  }}
                  disabled={loadingLista}
                  placeholder={loadingLista ? 'Cargando lista…' : 'Buscar por apellido, nombre o cédula…'}
                  autoComplete="off"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
                />
                {cedulaSeleccionada && (
                  <button
                    type="button"
                    onClick={limpiarPersona}
                    disabled={loadingLista}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500 hover:text-red-600 px-1"
                  >
                    Cambiar
                  </button>
                )}
              </div>
              {showPersonaResults && !cedulaSeleccionada && personaSearch.trim().length > 0 && (
                <div className="mt-1 bg-white border border-gray-200 rounded-lg shadow-sm max-h-64 overflow-y-auto">
                  {loadingLista ? (
                    <div className="px-3 py-2 text-xs text-gray-500 italic">Cargando…</div>
                  ) : resultadosPersona.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-gray-500 italic">Sin coincidencias</div>
                  ) : (
                    resultadosPersona.map((p) => (
                      <button
                        key={p.cedula}
                        type="button"
                        onClick={() => seleccionarPersona(p)}
                        className="w-full text-left px-3 py-2 text-xs hover:bg-indigo-50 border-b border-gray-100 last:border-b-0"
                      >
                        <div className="font-medium text-gray-900">{p.nombres || '—'}</div>
                      </button>
                    ))
                  )}
                </div>
              )}
              {loadingLista && <p className="text-xs text-gray-500 mt-1">Cargando lista…</p>}
            </div>

            {error && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>
            )}

            <button
              type="submit"
              disabled={loadingLista || !cedulaSeleccionada || loadingHistorial}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-gray-500 text-white font-medium text-sm hover:bg-gray-600 disabled:opacity-50 shadow-sm"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              {loadingHistorial ? 'Buscando…' : 'Buscar'}
            </button>
          </form>

          {personaConsultada && bloqueoJustificacionPropio && (
            <p className="mt-5 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              No puede registrar certificado médico de su propia cédula. Solicítelo a otro supervisor.
            </p>
          )}

          {personaConsultada && !bloqueoJustificacionPropio && !formularioRegistroAbierto && (
            <button
              type="button"
              onClick={abrirFormularioRegistro}
              className="mt-5 w-full py-3 rounded-xl bg-indigo-600 text-white font-medium text-sm hover:bg-indigo-700 shadow-sm"
            >
              Registrar certificado médico
            </button>
          )}

          {personaConsultada && !bloqueoJustificacionPropio && formularioRegistroAbierto && (
          <form onSubmit={solicitarConfirmacionRegistro} className="mt-5 space-y-5 border-t border-gray-100 pt-5">
            <p className="text-sm font-medium text-gray-800">
              Periodo para <span className="text-indigo-700">{nombrePersonaSeleccionada}</span>
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="fecha-desde" className="block text-sm font-medium text-gray-800 mb-1">
                  Desde <span className="text-red-600">*</span>
                </label>
                <input
                  id="fecha-desde"
                  type="date"
                  required
                  min={fechaMin}
                  max={fechaMax}
                  value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm"
                />
              </div>
              <div>
                <label htmlFor="fecha-hasta" className="block text-sm font-medium text-gray-800 mb-1">
                  Hasta <span className="text-red-600">*</span>
                </label>
                <input
                  id="fecha-hasta"
                  type="date"
                  required
                  min={fechaMin}
                  max={fechaMax}
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 -mt-2">
              Periodo permitido: {fechaMin} a {fechaMax} (hoy ± {CERT_PERIODO_MAX_DIAS} días) para Desde y Hasta.
            </p>

            <div>
              <p className="block text-sm font-medium text-gray-800 mb-2">
                Documento <span className="text-red-600">*</span>
                
              </p>
              {useDeviceCamera ? (
                <div className={esProduccion ? undefined : 'flex flex-col gap-2'}>
                  <button
                    type="button"
                    onClick={() => certInputCameraRef.current?.click()}
                    className="w-full py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
                  >
                    Tomar foto
                  </button>
                  {!esProduccion && (
                    <button
                      type="button"
                      onClick={() => certInputFileRef.current?.click()}
                      className="w-full py-2.5 rounded-lg border border-gray-300 text-gray-800 text-sm font-medium hover:bg-gray-50"
                    >
                      Galería o archivo
                    </button>
                  )}
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    type="button"
                    onClick={startCameraCertDesktop}
                    className="flex-1 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
                  >
                    Tomar foto
                  </button>
                  {!esProduccion && (
                    <button
                      type="button"
                      onClick={() => certInputFileRef.current?.click()}
                      className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-800 text-sm font-medium hover:bg-gray-50"
                    >
                      Galería o archivo
                    </button>
                  )}
                </div>
              )}
              <input
                ref={certInputCameraRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                capture="environment"
                className="hidden"
                onChange={handleCertFileFromInput}
              />
              <input
                ref={certInputFileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handleCertFileFromInput}
              />
              {cameraCertAbierta && !useDeviceCamera && (
                <div className="relative rounded-lg overflow-hidden bg-black mt-3">
                  <video ref={certVideoRef} className="w-full max-h-56 object-contain" playsInline muted />
                  <canvas ref={certCanvasRef} className="hidden" aria-hidden />
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-2">
                    <button
                      type="button"
                      onClick={async () => {
                        const f = await captureFromCertVideo();
                        if (f) {
                          setArchivo(f);
                          stopCameraCert();
                          setErrSubida('');
                        } else {
                          setErrSubida((prev) => prev || 'No se pudo capturar la imagen.');
                        }
                      }}
                      className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium shadow"
                    >
                      Capturar
                    </button>
                    <button
                      type="button"
                      onClick={stopCameraCert}
                      className="px-4 py-2 rounded-lg bg-white/90 text-gray-800 text-sm font-medium border"
                    >
                      Cerrar cámara
                    </button>
                  </div>
                </div>
              )}
              {archivo && (
                <p className="text-xs text-gray-600 mt-2 truncate">
                  Archivo: <strong>{archivo.name}</strong>
                </p>
              )}
              {previewObjectUrl && (
                <div className="mt-3 rounded-lg border border-gray-200 overflow-hidden bg-gray-50">
                  <img src={previewObjectUrl} alt="Vista previa" className="w-full max-h-48 object-contain" />
                </div>
              )}
            </div>

            {errSubida && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{errSubida}</p>
            )}

            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={cerrarFormularioRegistro}
                disabled={loadingSubida}
                className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-800 font-medium text-sm hover:bg-gray-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loadingSubida}
                className="flex-1 py-3 rounded-xl bg-indigo-600 text-white font-medium text-sm hover:bg-indigo-700 disabled:opacity-50 shadow-sm"
              >
                {loadingSubida ? 'Registrando…' : 'Guardar'}
              </button>
            </div>
          </form>
          )}
        </div>

        {personaConsultada && historial !== null && (
          <div className="mt-8 bg-white rounded-2xl shadow-md border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Certificados médicos registrados</h2>
            {loadingHistorial ? (
              <p className="text-sm text-gray-500">Cargando…</p>
            ) : historial.length === 0 ? (
              <p className="text-sm text-gray-600 py-4 text-center border border-dashed border-gray-200 rounded-lg">
                No hay certificados médicos registrados para esta persona.
              </p>
            ) : (
              <ul className="divide-y divide-gray-100 border border-gray-100 rounded-lg overflow-hidden">
                {historial.map((row) => (
                  <li key={row.id} className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-gray-50/50">
                    <div className="text-sm space-y-1">
                      <p>
                        <span className="text-gray-500">Periodo:</span>{' '}
                        <strong>{row.fecha_desde}</strong> — <strong>{row.fecha_hasta}</strong>
                      </p>
                      <p className="text-xs text-gray-600">
                        Registrado: {row.foto_subida_en || '—'} · {row.foto_subida_por || '—'}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => abrirVistaCertificado(row)}
                      className="text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 px-3 py-2 rounded-lg hover:bg-indigo-100"
                    >
                      Ver documento
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {vistaCertMeta != null && (
          <div
            className="fixed inset-0 z-[55] flex items-center justify-center p-4 bg-black/50"
            role="dialog"
            aria-modal
            onClick={cerrarVistaCertificado}
          >
            <div
              className="bg-white rounded-xl shadow-xl max-w-lg w-full p-5 space-y-4 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold text-gray-900">
                Certificado médico — {vistaCertMeta.nombrePersona}
              </h3>
              <dl className="text-sm space-y-2 border border-gray-100 rounded-lg p-3 bg-gray-50">
                <div>
                  <dt className="text-xs text-gray-500">Periodo</dt>
                  <dd className="font-medium">
                    {vistaCertMeta.fechaDesde} — {vistaCertMeta.fechaHasta}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-500">Registrado</dt>
                  <dd className="font-medium">{vistaCertMeta.subidoEn || '—'}</dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-500">Por</dt>
                  <dd className="font-medium">{vistaCertMeta.subidoPor || '—'}</dd>
                </div>
              </dl>
              <div className="rounded-lg border border-gray-200 overflow-hidden bg-gray-100 min-h-[120px] flex items-center justify-center">
                {vistaCertImg.loading && <p className="text-sm text-gray-600 py-8">Cargando…</p>}
                {vistaCertImg.error && !vistaCertImg.loading && (
                  <p className="text-sm text-red-600 px-3 py-6 text-center">{vistaCertImg.error}</p>
                )}
                {vistaCertImg.url && !vistaCertImg.loading && (
                  <img src={vistaCertImg.url} alt="Documento" className="w-full max-h-[70vh] object-contain" />
                )}
              </div>
              <button
                type="button"
                onClick={cerrarVistaCertificado}
                className="w-full py-2.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-800 hover:bg-gray-50"
              >
                Cerrar
              </button>
            </div>
          </div>
        )}

        {confirmarSubidaAbierta && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60" role="dialog" aria-modal>
            <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-5 space-y-4">
              <h4 className="text-base font-semibold text-gray-900">¿Está seguro?</h4>
              <p className="text-sm text-gray-700 leading-relaxed">
                Registrar certificado médico de <strong>{nombrePersonaSeleccionada}</strong> del{' '}
                <strong>{fechaDesde}</strong> al <strong>{fechaHasta}</strong>.
              </p>
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setConfirmarSubidaAbierta(false)}
                  disabled={loadingSubida}
                  className="flex-1 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 disabled:opacity-50"
                >
                  No
                </button>
                <button
                  type="button"
                  onClick={() => void ejecutarSubidaJustificacion()}
                  disabled={loadingSubida}
                  className="flex-1 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium disabled:opacity-50"
                >
                  {loadingSubida ? 'Guardando…' : 'Sí, registrar'}
                </button>
              </div>
            </div>
          </div>
        )}

        {modalExito && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/40" role="dialog" aria-modal>
            <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6 text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                <svg className="h-7 w-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Certificado médico registrado</h2>
              <p className="mt-2 text-sm text-gray-600">El documento se guardó correctamente.</p>
              <button
                type="button"
                onClick={() => setModalExito(false)}
                className="mt-5 w-full py-2 rounded-md text-white bg-indigo-600 hover:bg-indigo-700 text-sm font-medium"
              >
                Aceptar
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default CertificadoMultaFaltaPunto;
